import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Precise countdown timer with pause/resume support.
 * Uses Date.now() deltas to avoid drift.
 */
export const useTimer = (initialTime = 60, { onTimeUp } = {}) => {
  const [timeLeft, setTimeLeft] = useState(initialTime);
  const [isRunning, setIsRunning] = useState(false);

  const rafRef = useRef(null);
  const endTimeRef = useRef(null); // when the timer would reach 0
  const onTimeUpRef = useRef(onTimeUp);

  // Keep callback ref fresh without re-registering effect
  useEffect(() => {
    onTimeUpRef.current = onTimeUp;
  }, [onTimeUp]);

  const tick = useCallback(() => {
    const remaining = Math.max(0, endTimeRef.current - Date.now());
    const secs = Math.ceil(remaining / 1000);
    setTimeLeft(secs);

    if (remaining <= 0) {
      setIsRunning(false);
      onTimeUpRef.current?.();
      return;
    }
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const start = useCallback(() => {
    setIsRunning(true);
  }, []);

  const pause = useCallback(() => {
    setIsRunning(false);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  }, []);

  const reset = useCallback(
    (newTime) => {
      setIsRunning(false);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      const t = newTime ?? initialTime;
      setTimeLeft(t);
      endTimeRef.current = null;
    },
    [initialTime],
  );

  // Actually start the animation loop when isRunning flips to true
  useEffect(() => {
    if (!isRunning) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }
    // Set end time based on current timeLeft
    endTimeRef.current = Date.now() + timeLeft * 1000;
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRunning]);

  return { timeLeft, isRunning, start, pause, reset, setTimeLeft };
};
